import { type ReactNode } from 'react'
import { Link } from 'react-router'

import { Footer } from '../components/Footer'
import { Icon } from '../components/Icon'
import { Navbar } from '../components/Navbar'
import { ORG } from '../content/legal'

/**
 * `/contact` — M17-T17.
 *
 * The Local interface had no non-emergency way to reach AMTC: the concierge
 * form is Tourism-only and the only other route out of the app was the
 * emergency flow. That is the gap this page closes, and it is why the page is
 * deliberately NOT a form — a second intake would duplicate `TourismEnquiry`
 * and the admin enquiries queue that backs it, and Phase 1 has no queue for a
 * general-purpose contact message. Published details, no new write path.
 *
 * THE EMERGENCY NOTICE IS FIRST, and it is the one place on a non-emergency
 * page where `--color-sos` is correct: someone in trouble who lands here by
 * mistake must be redirected before they read anything else. It links to the
 * emergency flow rather than restating a number, so there is one place that
 * owns what to dial (ADR-0035).
 */
export function Contact(): ReactNode {
  const hasDetails = ORG.contactEmail !== null || ORG.phone !== null

  return (
    <main className="flex min-h-screen flex-col bg-bg-app font-sans text-ink-1">
      <Navbar />

      <div className="mx-auto w-full max-w-2xl px-5 py-10 sm:px-8">
        <h1 className="font-display text-d-sm font-medium tracking-display text-ink-1 sm:text-d-md">
          Contact AMTC
        </h1>
        <p className="mt-3 text-[18px] leading-7 text-ink-2">
          For questions about the platform, partner hospitals, or how your
          information is handled.
        </p>

        <div className="mt-6 flex items-start gap-3 rounded-r3 border border-sos bg-sos-bg px-5 py-4">
          <span className="mt-0.5 shrink-0 text-sos" aria-hidden="true">
            <Icon name="sos" size={20} />
          </span>
          <p className="text-[16px] leading-6 text-ink-1">
            <span className="font-medium">
              This page is not for medical emergencies.
            </span>{' '}
            If you need urgent care,{' '}
            <Link
              to="/emergency"
              className="text-sos underline hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              use the emergency page
            </Link>{' '}
            — it will not wait on a reply from us.
          </p>
        </div>

        {hasDetails ? (
          <section className="mt-8">
            <h2 className="text-[20px] font-medium leading-7 text-ink-1">
              Reach us
            </h2>
            <address className="mt-4 flex flex-col gap-3 rounded-r3 border border-line-1 bg-bg-soft px-5 py-4 text-[16px] leading-6 not-italic text-ink-1">
              {ORG.legalName !== null && (
                <span className="font-medium">{ORG.legalName}</span>
              )}
              {ORG.contactEmail !== null && (
                <a
                  href={`mailto:${ORG.contactEmail}`}
                  className="inline-flex items-center gap-2 text-accent-ink underline hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  <Icon name="chat" size={16} /> {ORG.contactEmail}
                </a>
              )}
              {ORG.phone !== null && (
                <a
                  href={`tel:${ORG.phone.replace(/\s/g, '')}`}
                  className="inline-flex items-center gap-2 text-accent-ink underline hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  <Icon name="phone" size={16} /> {ORG.phone}
                </a>
              )}
              {ORG.address !== null && (
                <span className="text-ink-2">{ORG.address}</span>
              )}
            </address>
          </section>
        ) : null}

        <section className="mt-8">
          <h2 className="text-[20px] font-medium leading-7 text-ink-1">
            Other ways through
          </h2>
          <ul className="mt-4 flex flex-col gap-3 text-[16px] leading-6 text-ink-2">
            <li>
              International patients can send a case to the AMTC concierge using
              the{' '}
              <Link
                to="/tourism/enquiry"
                className="text-accent-ink underline hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                enquiry form
              </Link>
              .
            </li>
            <li>
              Questions about your information are answered in the{' '}
              <Link
                to="/privacy"
                className="text-accent-ink underline hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                privacy policy
              </Link>
              .
            </li>
          </ul>
        </section>
      </div>

      <Footer />
    </main>
  )
}
